/**
 * Preview composer for Figma pin pieces in /public/icons/Pins.
 *
 * Pin body by Ofsted:
 *   Outstanding → Pin_Green
 *   Good → Pin_Blue_2
 *   Requires improvement → Pin_ReqImp_Amber
 *   Inadequate → Pin_InAdeq_Red
 *   (empty / not rated) → Pin_NA_Grey
 * Shadow: Pin_Base_New
 * Fill: Boys → Boys_Fill_New, Girls → Girls_Fill, Mixed → Mixed_Fill
 * Phase: Primary → Primary_Child (left), Secondary → Secondary_child (right),
 *        Both → both kids
 * Type badge: Grammar → Badge_G, Independent → Badge_I
 */

import { normaliseOfstedGradeForPin } from "./markerConstants";

const PINS_BASE = "/icons/Pins";

// ~1.3× larger than the original 40×46 Figma pin for map readability
export const FIGMA_PIN_WIDTH = 52;
export const FIGMA_PIN_HEIGHT = 65;

const PIN_BY_OFSTED = {
  Outstanding: "Pin_Green.svg",
  Good: "Pin_Blue_2.svg",
  "Requires improvement": "Pin_ReqImp_Amber.svg",
  Inadequate: "Pin_InAdeq_Red.svg",
};

const PIN_NOT_RATED = "Pin_NA_Grey.svg";
const PIN_SHADOW = "Pin_Base_New.svg";

const FILL_BY_GENDER = {
  Boys: "Boys_Fill_New.svg",
  Girls: "Girls_Fill.svg",
  Mixed: "Mixed_Fill.svg",
};

const BADGE_BY_TYPE = {
  Grammar: "Badge_G.svg",
  Independent: "Badge_I.svg",
};

function resolvePinFile(ofstedGrade) {
  const key = normaliseOfstedGradeForPin(ofstedGrade);
  if (!key) return PIN_NOT_RATED;
  return PIN_BY_OFSTED[key] || PIN_NOT_RATED;
}

/**
 * Returns true when we have Figma assets for this gender (+ any Ofsted, including blank).
 */
export function canUseFigmaPin({ gender }) {
  return Boolean(FILL_BY_GENDER[gender]);
}

/**
 * Compose layered HTML for Leaflet divIcon.
 * Layers (bottom → top): shadow → ofsted pin → gender fill → phase child → type badge.
 */
export function renderFigmaPinHtml({
  gender = "Mixed",
  phase = "S",
  ofstedGrade = "Outstanding",
  schoolType = "",
}) {
  const pinFile = resolvePinFile(ofstedGrade);
  const fillFile = FILL_BY_GENDER[gender] || FILL_BY_GENDER.Mixed;
  const badgeFile = BADGE_BY_TYPE[schoolType] || null;

  const isBoth = phase === "B";
  const isPrimary = phase === "P";
  const primarySize = 16 * 0.9; // ~90% of full primary child
  const secondaryWidth = 22 * 0.8; // ~80% width
  const secondaryHeight = secondaryWidth + 2; // tiny taller, width unchanged

  const pinBodyW = 52;
  const pinBodyH = 60;
  const fillSize = 30;
  const fillTop = 10;
  const fillLeft = (FIGMA_PIN_WIDTH - fillSize) / 2;
  // Trial: primary left half of fill, secondary right of centre
  const primaryLeft = fillLeft + (fillSize / 2 - primarySize) / 2;
  const secondaryLeft = Math.min(
    fillLeft + fillSize / 2 + (fillSize / 2 - secondaryWidth) / 2 - 2, // tiny nudge right
    fillLeft + fillSize - secondaryWidth // keep inside fill
  );
  const primaryTop = fillTop + (fillSize - primarySize) / 2;
  const secondaryTop = Math.max(
    fillTop + 1,
    fillTop + (fillSize - secondaryHeight) / 2
  );
  const shadowW = 18;
  const shadowH = 9;
  const shadowLeft = (FIGMA_PIN_WIDTH - shadowW) / 2;

  // Badge sits top-right of pin head
  const badgeW = 22;
  const badgeH = 13;
  const badgeLeft = FIGMA_PIN_WIDTH - badgeW - 1;
  const badgeTop = 2;

  const badgeHtml = badgeFile
    ? `<img class="v2-figma-pin-badge" src="${PINS_BASE}/${badgeFile}" alt=""
    style="position:absolute;left:${badgeLeft}px;top:${badgeTop}px;width:${badgeW}px;height:${badgeH}px;pointer-events:none;z-index:5;" />`
    : "";

  let childHtml = "";
  if (isBoth) {
    childHtml = `
  <img class="v2-figma-pin-child" src="${PINS_BASE}/Primary_Child.svg" alt=""
    style="position:absolute;left:${primaryLeft}px;top:${primaryTop}px;width:${primarySize}px;height:${primarySize}px;pointer-events:none;" />
  <img class="v2-figma-pin-child" src="${PINS_BASE}/Secondary_child.svg" alt=""
    style="position:absolute;left:${secondaryLeft}px;top:${secondaryTop}px;width:${secondaryWidth}px;height:${secondaryHeight}px;pointer-events:none;" />`;
  } else if (isPrimary) {
    childHtml = `
  <img class="v2-figma-pin-child" src="${PINS_BASE}/Primary_Child.svg" alt=""
    style="position:absolute;left:${primaryLeft}px;top:${primaryTop}px;width:${primarySize}px;height:${primarySize}px;pointer-events:none;" />`;
  } else {
    childHtml = `
  <img class="v2-figma-pin-child" src="${PINS_BASE}/Secondary_child.svg" alt=""
    style="position:absolute;left:${secondaryLeft}px;top:${secondaryTop}px;width:${secondaryWidth}px;height:${secondaryHeight}px;pointer-events:none;" />`;
  }

  return `
<div class="v2-figma-pin" style="position:relative;width:${FIGMA_PIN_WIDTH}px;height:${FIGMA_PIN_HEIGHT}px;overflow:visible;">
  <img class="v2-figma-pin-shadow" src="${PINS_BASE}/${PIN_SHADOW}" alt=""
    style="position:absolute;left:${shadowLeft}px;bottom:0;width:${shadowW}px;height:${shadowH}px;pointer-events:none;" />
  <img class="v2-figma-pin-body" src="${PINS_BASE}/${pinFile}" alt=""
    style="position:absolute;left:0;top:0;width:${pinBodyW}px;height:${pinBodyH}px;pointer-events:none;" />
  <img class="v2-figma-pin-fill" src="${PINS_BASE}/${fillFile}" alt=""
    style="position:absolute;left:${fillLeft}px;top:${fillTop}px;width:${fillSize}px;height:${fillSize}px;pointer-events:none;" />
  ${childHtml}
  ${badgeHtml}
</div>`.trim();
}
